// Overview "Registrations by session": the Registrations card's per-registration tally, split by session.
import { SESSIONS } from "@/lib/fields";
import { segmentCounts, zero } from "./overview";
import type { Person } from "./people";
import type { PStatus } from "./status";

export interface SessionTail {
  /** read + delivered + on the way */
  sent: number;
  /** over the limit */
  retry: number;
  /** failed or invalid */
  failed: number;
  /** not sent yet */
  left: number;
}

export interface SessionRow {
  /** session key ("16 Morning"), or "unknown" */
  key: string;
  label: string;
  /** Puja start, "" for unknown */
  time: string;
  total: number;
  seg: ReturnType<typeof segmentCounts>;
  tail: SessionTail;
}

export interface SessionBreakdown {
  /** one row per session in date order, plus "unknown" only when it has registrations */
  rows: SessionRow[];
  /** registrations counted, each once (= the Registrations card total) */
  registrations: number;
  /** registrations with at least one known session; 0 = the snapshot has no sessions yet */
  known: number;
}

const segN = (seg: ReturnType<typeof segmentCounts>, key: string) => seg.find((s) => s.key === key)?.n ?? 0;

export function sessionTail(seg: ReturnType<typeof segmentCounts>): SessionTail {
  return {
    sent: segN(seg, "read") + segN(seg, "unread") + segN(seg, "onway"),
    retry: segN(seg, "over_limit"),
    failed: segN(seg, "fix"),
    left: segN(seg, "queued"),
  };
}

/** "234 sent · 9 retry · 5 failed · 0 left": sent and left always, retry and failed only when > 0. */
export function tailText(t: SessionTail, fmt: (n: number) => string = String): string {
  return [
    `${fmt(t.sent)} sent`,
    t.retry ? `${fmt(t.retry)} retry` : null,
    t.failed ? `${fmt(t.failed)} failed` : null,
    `${fmt(t.left)} left`,
  ]
    .filter(Boolean)
    .join(" · ");
}

function toRow(key: string, label: string, time: string, by: Record<PStatus, number>): SessionRow {
  const seg = segmentCounts(by);
  return { key, label, time, total: seg.reduce((n, s) => n + s.n, 0), seg, tail: sessionTail(seg) };
}

/**
 * Same registrations and status rule as overviewCounts().regs (in the sheet, not a duplicate; an opted-out
 * phone wins). A registration attending several sessions counts in each. No known session → "unknown".
 */
export function sessionBreakdown(people: Person[], sessions = SESSIONS): SessionBreakdown {
  const by = new Map(sessions.map((s) => [s.key, zero()]));
  const unknown = zero();
  let registrations = 0;
  let unknownN = 0;
  for (const p of people) {
    for (const r of p.regs) {
      if (!r.inSheet || r.status == null) continue;
      registrations++;
      const st: PStatus = p.optedOut ? "opted_out" : r.status;
      const hits = new Set(r.sessions.filter((k) => by.has(k)));
      if (!hits.size) {
        unknown[st]++;
        unknownN++;
      }
      for (const k of hits) (by.get(k) as Record<PStatus, number>)[st]++;
    }
  }
  const rows = sessions.map((s) => toRow(s.key, s.label, s.time, by.get(s.key) as Record<PStatus, number>));
  if (unknownN) rows.push(toRow("unknown", "Unknown", "", unknown));
  return { rows, registrations, known: registrations - unknownN };
}
